/**
 * Super-admin impersonation client hooks (M23).
 *
 * - useStartImpersonation     POST /admin/impersonate/:tenantId
 * - useExitImpersonation      POST /admin/impersonate/exit
 * - useImpersonationHistory   GET  /admin/impersonation-sessions
 *
 * The hooks DO NOT manage `localStorage` themselves — token swap,
 * `priorToken` capture, and navigation are the caller's responsibility
 * (see `pages/admin/SuperAdminPage.tsx` and `components/admin/ImpersonationBanner.tsx`).
 */
import {
  ImpersonationHistoryResponseSchema,
  StartImpersonationResponseSchema,
  type ImpersonationSessionDetail,
  type StartImpersonationResponse,
} from '@hrms/contracts/admin';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Re-exported types for page consumption
// ---------------------------------------------------------------------------

export type { ImpersonationSessionDetail, StartImpersonationResponse };

// ---------------------------------------------------------------------------
// localStorage keys + helpers
// ---------------------------------------------------------------------------

export const IMPERSONATION_STATE_KEY = 'impersonation';

/**
 * Shape persisted in `localStorage[IMPERSONATION_STATE_KEY]` while a super-
 * admin is impersonating a tenant. Read by `ImpersonationBanner` to decide
 * whether to render itself.
 */
export interface ImpersonationLocalState {
  active: true;
  sessionId: string;
  priorToken: string;
  tenantName: string;
  userEmail: string;
  expiresAt: string;
}

export function readImpersonationState(): ImpersonationLocalState | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationLocalState;
    return parsed.active ? parsed : null;
  } catch {
    return null;
  }
}

export function clearImpersonationState(): void {
  localStorage.removeItem(IMPERSONATION_STATE_KEY);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useStartImpersonation() {
  return useMutation({
    mutationFn: async (tenantId: string): Promise<StartImpersonationResponse> => {
      return apiClient.post(`/admin/impersonate/${tenantId}`, {}, StartImpersonationResponseSchema);
    },
  });
}

const ExitResponseSchema = z.unknown();

export function useExitImpersonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (impersonationToken: string): Promise<void> => {
      await apiClient.post('/admin/impersonate/exit', {}, ExitResponseSchema, {
        // Override the token provider so the request goes out under the
        // impersonation JWT even if the auth store has already been
        // restored to the prior token.
        headers: { Authorization: `Bearer ${impersonationToken}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'impersonation-sessions'] });
    },
  });
}

export function useImpersonationHistory() {
  return useQuery({
    queryKey: ['admin', 'impersonation-sessions'],
    queryFn: async (): Promise<ImpersonationSessionDetail[]> => {
      const result = await apiClient.get(
        '/admin/impersonation-sessions',
        ImpersonationHistoryResponseSchema,
      );
      return result.data;
    },
  });
}
